import { useCallback, useRef } from 'react'

import { retry } from '@bettermode/common/retry'
import type { AuthToken } from '@tribeplatform/gql-client/types'
import { getDomainInfo } from '@tribeplatform/react-components/common/utils'

const RETRY_ATTEMPTS = 10
const RETRY_DELAY_MS = 2_000
const RETRY_BACKOFF = 1.5

interface UseRotateAccessToken {
  domain: string
  domainSubfolder: string
}

export const useRotateAccessToken = ({
  domain,
  domainSubfolder,
}: UseRotateAccessToken) => {
  const ongoingAttempt = useRef<Promise<string | null> | null>(null)

  const rotateAccessToken = useCallback(async (): Promise<string | null> => {
    if (ongoingAttempt.current) {
      return ongoingAttempt.current
    }

    const { getResourceUrl } = getDomainInfo({
      domain,
      domainSubfolder,
    })

    const attempt = async (): Promise<string> => {
      const nonce = Date.now()
      const response = await fetch(
        getResourceUrl(`/api/auth/refresh-token?nonce=${nonce}`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-store',
          },
          cache: 'no-store',
          body: JSON.stringify({ domain }),
        },
      )

      if (!response.ok) {
        throw new Error(`Failed to refresh access token: ${response.status}`)
      }

      const data = (await response.json()) as AuthToken
      return data.accessToken
    }

    const retryableAttempt = (): Promise<string | null> =>
      retry(attempt, {
        retries: RETRY_ATTEMPTS,
        delayMs: RETRY_DELAY_MS,
        backoff: RETRY_BACKOFF,
        onFailure: e => {
          /**
           * Passing callback instead of catch, so that the failure does not bubble up and null token is returned
           */
          logger.warn('Could not refresh access token after retries', e)
        },
      })
        .then(token => token ?? null)
        .finally(() => {
          ongoingAttempt.current = null
        })

    if (typeof window !== 'undefined' && !window.navigator?.onLine) {
      const runWhenOnline = (): Promise<string | null> =>
        new Promise(resolve => {
          const run = () => {
            resolve(retryableAttempt())
          }
          window.addEventListener('online', run, { once: true })
        })
      ongoingAttempt.current = runWhenOnline()
    } else {
      ongoingAttempt.current = retryableAttempt()
    }

    return ongoingAttempt.current
  }, [domain, domainSubfolder])

  return rotateAccessToken
}
